import styled from '@emotion/styled';
import {
  palette,
  radius,
  spacing,
  shadow,
  typography,
  layout,
  duration,
  easing,
} from '../../style-utils/theme';
import { shimmerLoop, skeletonBackground } from '../../style-utils/motion';
import { touch } from '../../style-utils/media-queries';

/* Card panel holding the selected building's details as a compact vertical
   list — one field under the next, hairline-divided. Fully fluid. */
export const BuildingDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  overflow: hidden;
`;

export const BuildingPicker = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  background: ${palette.irisTintSoft};
  border-bottom: 1px solid ${palette.line};
`;

export const BuildingPickerLabel = styled.label`
  color: ${palette.inkSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
`;

export const BuildingSelect = styled.select`
  width: 100%;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  /* ONE BOX, NOT THREE. This control sits inside the tinted picker band inside
     the sidebar card's own border, and with lineStrong + a drop shadow it drew
     a third, heavier frame — the "double borders on selects" report
     (2026-09-02). The hairline keeps the affordance; the band and the card do
     the rest of the framing. */
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  /* 16px ON A FINGER. iOS zooms the whole page when a text control under 16px
     takes focus and does not zoom back, so a 14px field throws the reader into
     a horizontally-scrolled layout they then have to pinch out of. Measured by
     scripts/analytics/responsive.js on every touch shape. */
  ${touch} {
    font-size: 16px;
  }

  transition: border-color ${duration.fast} ${easing.standard},
    box-shadow ${duration.fast} ${easing.standard};

  &:hover:not(:disabled) {
    border-color: ${palette.lagoon};
  }

  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    cursor: default;
    color: ${palette.slate};
    background: ${palette.surfaceMuted};
  }
`;

export const BuildingPickerHint = styled.p`
  margin: 0;
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
`;

export const BuildingEmpty = styled.p`
  margin: 0;
  padding: ${spacing.sm};
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
`;

/* One label/value pair, label stacked above value. */
export const BuildingRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: ${spacing.xs} ${spacing.sm};
  min-width: 0;

  & + & {
    border-top: 1px solid ${palette.line};
  }
`;

export const BuildingLabel = styled.div`
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
`;

export const BuildingValue = styled.div`
  min-width: 0;
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  letter-spacing: -0.011em;
  line-height: ${typography.leading.snug};
  word-break: break-word;
`;

/* Shimmer skeleton row — shown while the building is still loading. */
export const SkeletonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  min-width: 0;

  & + & {
    border-top: 1px solid ${palette.line};
  }
`;

export const SkeletonLine = styled.div<{ w?: string }>`
  height: 12px;
  width: ${({ w }) => w || '100%'};
  border-radius: ${radius.xs};
  ${skeletonBackground}
  ${shimmerLoop}
`;
